import Vue from 'vue'
import VueRouter from 'vue-router'
import PagePlaceholder from '@/views/PagePlaceholder'
import SignUp from '@/views/SignUp'
import Login from '@/views/Login'
import ForgotPassword from '@/views/ForgotPassword'
import ResetPassword from '@/views/ResetPassword'
// import AuthenticatedRedirect from '@/views/AuthenticatedRedirect'
import UserHomeRedirect from '@/views/UserHomeRedirect'
import PreLogout from '@/views/PreLogout'
import SessionExpired from '@/views/SessionExpired'
import { authGuard } from '@/auth'

Vue.use(VueRouter)

const routes = [
  // LOGIN ROUTE ----------------------------------------
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { label: 'Login', requiresAuth: false }
  },
  // PASSWORD RESET ROUTES ----------------------------------------
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { label: 'Forgot Password', requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { label: 'Reset Password', requiresAuth: false }
  },
  // Default route redirects to login
  {
    path: '/',
    redirect: '/login'
  },
  // Shared pages (also on marketing site)
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    props: true,
    component: () => import('@/views/PrivacyPolicy.vue'),
    meta: { label: 'Privacy Policy', requiresAuth: false }
  },
  {
    path: '/frequently-asked-questions',
    name: 'FaqView',
    props: true,
    component: () => import('@/views/FaqView.vue'),
    meta: { label: 'FAQ', requiresAuth: false }
  },
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditions',
    props: true,
    component: () => import('@/views/TermsAndConditions.vue'),
    meta: { label: 'Terms and Conditions', requiresAuth: false }
  },
  {
    path: '/user-home-redirect',
    name: 'UserHomeRedirect',
    component: UserHomeRedirect,
    beforeEnter: authGuard
  },
  {
    path: '/clear',
    name: 'ClearSession',
    meta: { requiresAuth: false },
    beforeEnter (to, from, next) {
      // Collect COMPREHENSIVE diagnostic snapshot BEFORE clearing anything
      const diagnostics = {
        // ===== BROWSER & DEVICE =====
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        languages: navigator.languages ? navigator.languages.join(', ') : 'unknown',
        cookiesEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
        maxTouchPoints: navigator.maxTouchPoints || 0,
        onLine: navigator.onLine,
        // Network info (Chrome/Edge)
        connectionType: navigator.connection?.effectiveType || 'unknown',
        connectionDownlink: navigator.connection?.downlink || 'unknown',
        connectionRtt: navigator.connection?.rtt || 'unknown',
        // Screen
        screenWidth: screen.width,
        screenHeight: screen.height,
        screenAvailWidth: screen.availWidth,
        screenAvailHeight: screen.availHeight,
        devicePixelRatio: window.devicePixelRatio,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        colorDepth: screen.colorDepth,

        // ===== NAVIGATION & HISTORY =====
        currentUrl: window.location.href,
        currentOrigin: window.location.origin,
        currentHostname: window.location.hostname,
        currentPathname: window.location.pathname,
        currentSearch: window.location.search,
        currentHash: window.location.hash,
        previousVueRoute: from ? from.fullPath : 'none',
        previousVueRouteName: from ? from.name : 'none',
        referrer: document.referrer || 'none',
        historyLength: window.history.length,
        // Performance navigation (was page loaded from cache/back-forward?)
        navigationType: (() => {
          try {
            const nav = performance.getEntriesByType('navigation')[0]
            return nav ? nav.type : 'unknown'
          } catch (e) { return 'error: ' + e.message }
        })(),

        // ===== COOKIES (detailed) =====
        cookieCount: document.cookie.split(';').filter(c => c.trim()).length,
        cookieRaw: document.cookie || '(empty)',
        cookies: document.cookie.split(';').filter(c => c.trim()).map(c => {
          const parts = c.trim().split('=')
          return { name: parts[0], value: parts.slice(1).join('=').substring(0, 300) }
        }),
        // Specific cookie checks
        hasConnectSid: document.cookie.includes('connect.sid'),
        hasOvbLoggedIn: document.cookie.includes('ovb-logged-in'),
        ovbLoggedInValue: (() => {
          const match = document.cookie.match(/ovb-logged-in=([^;]*)/)
          return match ? match[1] : 'not-found'
        })(),

        // ===== LOCAL STORAGE (full dump, truncated values) =====
        localStorageCount: (() => { try { return localStorage.length } catch (e) { return 'blocked: ' + e.message } })(),
        localStorage: (() => {
          try {
            const items = {}
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i)
              const val = localStorage.getItem(key)
              items[key] = val && val.length > 1000 ? val.substring(0, 1000) + '...[truncated ' + val.length + ' chars]' : val
            }
            return JSON.stringify(items, null, 2)
          } catch (e) { return 'error reading: ' + e.message }
        })(),

        // ===== SESSION STORAGE =====
        sessionStorageCount: (() => { try { return sessionStorage.length } catch (e) { return 'blocked: ' + e.message } })(),
        sessionStorage: (() => {
          try {
            const items = {}
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i)
              const val = sessionStorage.getItem(key)
              items[key] = val && val.length > 1000 ? val.substring(0, 1000) + '...[truncated ' + val.length + ' chars]' : val
            }
            return JSON.stringify(items, null, 2)
          } catch (e) { return 'error reading: ' + e.message }
        })(),

        // ===== VUEX PERSISTED STATE (full user object) =====
        vuexState: (() => {
          try {
            const raw = localStorage.getItem('vuex')
            if (!raw) return 'no vuex in localStorage'
            const vuex = JSON.parse(raw)
            return JSON.stringify({
              user: vuex.user || 'missing',
              application: vuex.application || 'missing'
            }, null, 2)
          } catch (e) { return 'error: ' + e.message }
        })(),

        // ===== VUE AUTH INSTANCE STATE =====
        authIsAuthenticated: window.__vue_auth_instance__?.isAuthenticated ?? 'unavailable',
        authUser: (() => {
          try {
            const u = window.__vue_auth_instance__?.user
            if (!u) return 'no auth user'
            return JSON.stringify({ username: u.username, appUserId: u.appUserId, permissionKey: u.permissionKey, appTenantId: u.appTenantId, homePath: u.homePath })
          } catch (e) { return 'error: ' + e.message }
        })(),
        authLoading: window.__vue_auth_instance__?.loading ?? 'unavailable',
        authError: window.__vue_auth_instance__?.error ? String(window.__vue_auth_instance__.error) : 'none',

        // ===== USERNAME (best effort from multiple sources) =====
        username: (() => {
          try {
            // Try auth instance first
            if (window.__vue_auth_instance__?.user?.username) return window.__vue_auth_instance__.user.username
            // Try Vuex
            const vuex = JSON.parse(localStorage.getItem('vuex') || '{}')
            if (vuex.user?.user?.username) return vuex.user.user.username
            return 'unknown'
          } catch (e) { return 'unknown' }
        })(),

        // ===== PERFORMANCE TIMING =====
        performanceTiming: (() => {
          try {
            const nav = performance.getEntriesByType('navigation')[0]
            if (!nav) return 'unavailable'
            return JSON.stringify({
              dns: Math.round(nav.domainLookupEnd - nav.domainLookupStart) + 'ms',
              tcp: Math.round(nav.connectEnd - nav.connectStart) + 'ms',
              ttfb: Math.round(nav.responseStart - nav.requestStart) + 'ms',
              pageLoad: Math.round(nav.loadEventEnd - nav.startTime) + 'ms',
              domContentLoaded: Math.round(nav.domContentLoadedEventEnd - nav.startTime) + 'ms',
              redirectCount: nav.redirectCount,
              transferSize: nav.transferSize + ' bytes',
              protocol: nav.nextHopProtocol
            })
          } catch (e) { return 'error: ' + e.message }
        })(),

        // ===== SERVICE WORKERS =====
        serviceWorkerStatus: (() => {
          try {
            if (!navigator.serviceWorker) return 'not supported'
            if (navigator.serviceWorker.controller) return 'active: ' + navigator.serviceWorker.controller.scriptURL
            return 'registered but no controller'
          } catch (e) { return 'error: ' + e.message }
        })(),

        // ===== INDEXED DB =====
        indexedDBDatabases: (() => {
          try {
            if (!indexedDB.databases) return 'databases() not supported'
            // This is async but we'll try synchronously — if empty it returns fast
            return 'check pending (async)'
          } catch (e) { return 'error: ' + e.message }
        })(),

        // ===== CACHE STORAGE =====
        cacheStorageAvailable: 'caches' in window,

        // ===== LIVE /api/auth/me PROBE =====
        // This will be filled in asynchronously before sending
        meProbe: 'pending',

        // ===== TIMESTAMPS =====
        clientTime: new Date().toISOString(),
        timezoneOffset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        pageLoadedAt: (() => {
          try { return new Date(performance.timeOrigin).toISOString() } catch (e) { return 'unknown' }
        })(),
        timeOnPage: (() => {
          try { return Math.round(performance.now() / 1000) + ' seconds' } catch (e) { return 'unknown' }
        })()
      }

      // Do a live /api/auth/me probe to capture exactly what the server returns RIGHT NOW
      // Then send full diagnostics
      fetch('/api/auth/me', { credentials: 'include' })
        .then(r => r.text().then(body => ({ status: r.status, statusText: r.statusText, headers: Object.fromEntries(r.headers.entries()), body: body.substring(0, 1000) })))
        .catch(e => ({ status: 'fetch-error', error: e.message }))
        .then(meResult => {
          diagnostics.meProbe = JSON.stringify(meResult)

          // Also try to enumerate IndexedDB databases
          const idbPromise = (indexedDB.databases ? indexedDB.databases() : Promise.resolve('not supported'))
            .then(dbs => { diagnostics.indexedDBDatabases = JSON.stringify(dbs.map(d => d.name)) })
            .catch(e => { diagnostics.indexedDBDatabases = 'error: ' + e.message })

          // Enumerate cache storage keys
          const cachePromise = ('caches' in window ? caches.keys() : Promise.resolve([]))
            .then(keys => { diagnostics.cacheStorageKeys = JSON.stringify(keys) })
            .catch(e => { diagnostics.cacheStorageKeys = 'error: ' + e.message })

          return Promise.all([idbPromise, cachePromise])
        })
        .then(() => {
          // Send complete diagnostics to backend
          return fetch('/api/auth/clear-diagnostics', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(diagnostics)
          })
        })
        .catch(() => {}) // Don't block clear if diagnostics fail
        .finally(() => {
          // Now destroy session and clear everything
          fetch('/logout', { credentials: 'include' })
            .catch(() => {})
            .finally(() => {
              const domains = [window.location.hostname, '.ourvisualbrain.com']
              document.cookie.split(';').forEach(c => {
                const name = c.trim().split('=')[0]
                domains.forEach(d => {
                  document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + d
                })
                document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/'
              })
              localStorage.clear()
              sessionStorage.clear()
              window.location.href = '/login'
            })
        })
    }
  },
  {
    path: '/no-access',
    name: 'NoAccess',
    component: () => import('@/views/NoAccess.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/pre-logout',
    name: 'PreLogout',
    component: PreLogout
  },
  {
    path: '/session-expired',
    name: 'SessionExpired',
    component: SessionExpired
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: SignUp
  },
  {
    path: '/raw-user',
    name: 'RawUser',
    props: true,
    component: () => import('@/views/RawUser.vue'),
    beforeEnter: authGuard,
    // Has correct permissions
    meta: { needsPermission: ['p:super-admin', 'm:clinic', 'm:patient'], label: 'raw-user', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'raw-user' } }
  },
  {
    path: '/admin/application-manager',
    name: 'ApplicationManager',
    props: true,
    component: () => import('@/views/admin/applicationManager/ApplicationManager'),
    beforeEnter: authGuard,
    // Has correct permissions
    meta: { needsPermission: ['p:super-admin'], label: 'Application Manager', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'application-manager' } }
  },
  {
    path: '/admin/application-manager/app-route/:appRouteId',
    name: 'AppRouteDetail',
    props: true,
    component: () => import('@/views/admin/applicationManager/AppRouteDetail'),
    beforeEnter: authGuard,
    // Has correct permissions
    meta: { needsPermission: ['p:super-admin'], label: 'App Route', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'appRouteId' } }
  },
  {
    path: '/admin/notifications',
    name: 'NotificationManager',
    props: true,
    component: () => import('@/views/admin/Notifications/NotificationManager'),
    beforeEnter: authGuard,
    // works for super-admin
    meta: { needsPermission: ['p:super-admin'], label: 'Notification Manager', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'notification-manager' } }
  },
  {
    path: '/admin/notifications/create',
    name: 'NotificationBuilder',
    props: true,
    component: () => import('@/views/admin/Notifications/NotificationBuilder'),
    beforeEnter: authGuard,
    // works for super-admin
    meta: { needsPermission: ['p:super-admin'], label: 'Create Notification', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'create-notification' } }
  },
  {
    path: '/admin/notifications/edit/:id',
    name: 'EditNotification',
    props: true,
    component: () => import('@/views/admin/Notifications/EditNotification'),
    beforeEnter: authGuard,
    // works for super-admin
    meta: { needsPermission: ['p:super-admin'], label: 'Edit Notification', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'edit-notification' } }
  },
  // {
  //   path: '/admin/old-customers',
  //   name: 'OldCustomers',
  //   props: true,
  //   component: () => import('@/views/admin/oldCustomers/OldDataImport'),
  //   beforeEnter: authGuard,
  //   meta: { needsPermission: ['p:super-admin'], label: 'Old Customers', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'old-customers' } }
  // },
  // {
  //   path: '/admin/old-customer/:companyId',
  //   name: 'OldCustomerDetail',
  //   props: true,
  //   component: () => import('@/views/admin/oldCustomers/OldCustomerDetail'),
  //   beforeEnter: authGuard,
  //   meta: { needsPermission: [], label: 'Old Customer Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'old-customer-detail' } }
  // },
  {
    path: '/help-center-admin',
    name: 'HelpCenterAdmin',
    props: true,
    component: () => import('@/views/clinic/HelpCenterAdmin'),
    beforeEnter: authGuard,
    // works for super-admin
    meta: { needsPermission: ['p:super-admin'], label: 'Help CenterAdmin', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'admin-help-center' } }
  },
  {
    path: '/help-center-admin/help-request/:userHelpRequestId',
    name: 'AdminHelpRequest',
    props: true,
    component: () => import('@/views/clinic/HelpRequest'),
    beforeEnter: authGuard,
    // works for super-admin
    meta: { needsPermission: ['p:super-admin'], label: 'Admin Help Request', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'admin-help-request' } }
  },
  {
    path: '/help-center',
    name: 'HelpCenter',
    props: true,
    component: () => import('@/views/clinic/HelpCenter'),
    beforeEnter: authGuard,
    // works for clinicians
    meta: { needsPermission: ['m:clinic'], label: 'Help Center', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'help-center' } }
  },
  {
    path: '/help-center/help-request/:userHelpRequestId',
    name: 'HelpRequest',
    props: true,
    component: () => import('@/views/clinic/HelpRequest'),
    beforeEnter: authGuard,
    // works for clinicians
    meta: { needsPermission: ['m:clinic'], label: 'Help Request', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'help-request' } }
  },
  // {
  //   path: '/admin/old-user/:memberId',
  //   name: 'OldUserDetail',
  //   props: true,
  //   component: () => import('@/views/admin/oldCustomers/OldUserDetail.vue'),
  //   beforeEnter: authGuard,
  //   meta: { needsPermission: [], label: 'Old User Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'old-user-detail' } }
  // },
  // {
  //   path: '/admin/old-users',
  //   name: 'OldUsers',
  //   props: true,
  //   component: () => import('@/views/admin/oldCustomers/OldUsers.vue'),
  //   beforeEnter: authGuard,
  //   meta: { needsPermission: [], label: 'Old Users', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'old-users' } }
  // },
  {
    path: '/admin/activity-session-explorer',
    name: 'ActivitySessionExplorer',
    props: true,
    component: () => import('@//views/admin/ovbDashboard/components/ActivitySessionExplorer/ActivitySessionExplorer.vue'),
    beforeEnter: authGuard,
    // good
    meta: { needsPermission: ['p:super-admin'], label: 'Activity Sessions', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-session-explorer' } }
  },
  {
    path: '/admin/activity-session-explorer/session-detail/:sessionId',
    name: 'AdminActivitySessionViewer',
    props: true,
    component: () => import('@/views/admin/ovbDashboard/components/ActivitySessionExplorer/ActivitySessionViewer.vue'),
    beforeEnter: authGuard,
    // good
    meta: { needsPermission: ['p:super-admin'], label: 'Activity Session', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-session' } }
  },
  {
    path: '/clinic/activity-sessions',
    name: 'ClinicSessionManager',
    props: true,
    component: () => import('@/views/admin/clinicSessionManager/ClinicSessionManager.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin', 'p:support'], label: 'Activity Sessions', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-sessions' } }
  },
  {
    path: '/clinic/assignment-review',
    name: 'ActivityAssignmentReview',
    props: true,
    component: () => import('@/views/admin/activityAssignmentReview/ActivityAssignmentReview.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin', 'p:support'], label: 'Assignment Review', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'assignment-review' } }
  },
  {
    path: '/clinic/activity-sessions/session-detail/:sessionId',
    name: 'ClinicActivitySessionViewer',
    props: true,
    component: () => import('@/views/admin/clinicSessionManager/ActivitySessionViewer.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin', 'p:support'], label: 'Activity Session', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'session-detail' } }
  },
  {
    path: '/admin/saas-agreement-manager',
    name: 'EulaManager',
    props: true,
    component: () => import('@/views/admin/eula/EulaManager'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin'], label: 'SaaS Agreement Mgr', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'saas-agreement-manager' } }
  },
  {
    path: '/saas-agreement',
    name: 'SignAppEula',
    props: true,
    component: () => import('@/views/admin/eula/SignAppEula'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'SaaS Agreement', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'saas-agreement' } }
  },
  {
    path: '/signed-eula/:signedEulaId',
    name: 'SignedEulaDetail',
    props: true,
    component: () => import('@/views/admin/eula/SignedEulaDetail'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin', 'p:support'], label: 'Signed SaaS Agreement', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'signed-saas-agreement' } }
  },
  {
    path: '/admin/license-pack',
    name: 'LicensePackManager',
    props: true,
    component: () => import('@/views/admin/licensePackManager/LicensePackManager'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'License Pack', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'license-pack' } }
  },
  {
    path: '/admin/license-pack/:licensePackId',
    name: 'LicensePack',
    props: true,
    component: () => import('@/views/admin/licensePackManager/LicensePack'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'License Pack Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'license-pack-details' } }
  },
  {
    path: '/admin/app-tenant-manager',
    name: 'AppTenantManager',
    props: true,
    component: () => import('@/views/admin/appTenantManager/AppTenantManager'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'App Tenant Manager', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'app-tenant-manager' } }
  },
  {
    path: '/admin/app-tenant-manager/app-tenant/:appTenantName/:appTenantId',
    name: 'AppTenant',
    props: true,
    component: () => import('@/views/admin/appTenantManager/AppTenant'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'App Tenant Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'appTenantName' } }
  },
  {
    path: '/admin/workflow',
    name: 'ProjectManager',
    props: true,
    component: () => import('@/views/admin/projectManager/ProjectManager'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'Projects', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'projects' } }
  },
  {
    path: '/admin/workflow/template/:projectId',
    name: 'ProjectManagerTemplateDetail',
    props: true,
    component: () => import('@/views/admin/projectManager/ProjectManagerTemplateDetail'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'Project Template Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'project-template' } }
  },
  {
    path: '/admin/workflow/instance/:projectId',
    name: 'ProjectManagerInstanceDetail',
    props: true,
    component: () => import('@/views/admin/projectManager/ProjectManagerInstanceDetail'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'Project Instance Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'project-instance' } }
  },
  {
    path: '/admin/images',
    name: 'ImageManager',
    props: true,
    component: () => import('@/views/admin/imageManager/ImageManager'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'Images', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'images' } }
  },
  {
    path: '/admin/subscriptions',
    name: 'MySubscription',
    props: true,
    component: () => import('@/views/admin/appTenantManager/MySubscription'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'm:clinic-admin'], label: 'Subscriptions', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'subscriptions' } }
  },
  {
    path: '/admin/subscriptions-test',
    name: 'MySubscriptionTest',
    props: true,
    component: () => import('@/views/admin/appTenantManager/SubscriptionView.vue'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'm:clinic-admin'], label: 'Subscriptions', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'subscriptions-test' } }
  },
  {
    path: '/admin/billing',
    name: 'Billing',
    props: true,
    component: () => import('@/views/admin/appTenantManager/Billing'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'Billing', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'billing' } }
  },
  {
    path: '/admin/settings',
    name: 'AppTenantSettings',
    props: true,
    component: () => import('@/views/admin/appTenantManager/AppTenantSettings'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'Settings', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'settings' } }
  },
  {
    path: '/admin/users',
    name: 'AppTenantAppUsers',
    props: true,
    component: () => import('@/views/admin/appTenantManager/AppTenantAppUsers'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'Users', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'users' } }
  },
  {
    path: '/admin/guest-users',
    name: 'GuestUsers',
    props: true,
    component: () => import('@/views/admin/appTenantManager/GuestUsers'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'Guest Users', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'guest-users' } }
  },
  {
    path: '/admin/users/:userName/:appUserId',
    name: 'AppUser',
    props: true,
    component: () => import('@/views/admin/appTenantManager/AppUser'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'App User Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'userName' } }
  },
  {
    path: '/admin/guest-users/:userName/:appUserId',
    name: 'GuestUser',
    props: true,
    component: () => import('@/views/admin/appTenantManager/AppUser'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'App User Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'userName' } }
  },
  {
    path: '/admin/subscriptions/:appTenantSubscriptionId',
    name: 'AppTenantSubscription',
    props: true,
    component: () => import('@/views/admin/appTenantSubscription/AppTenantSubscription'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'App Tenant Subscription', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'appTenantSubscriptionId' } }
  },
  {
    path: '/admin/license/:licenseId',
    name: 'LicenseDetail',
    props: true,
    component: () => import('@/views/admin/appTenantManager/LicenseDetail'),
    beforeEnter: authGuard,
    // anything starting with /admin should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:support'], label: 'License Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'licenseId' } }
  },
  {
    path: '/ovb-dashboard',
    name: 'OvbDashboard',
    props: true,
    component: () => import('@/views/admin/newDashboard/NewDashboard'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin'], label: 'OVB Dashboard', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'ovb-dashboard' } }
  },
  {
    path: '/activity-errors',
    name: 'ActivityErrors',
    props: true,
    component: () => import('@/views/admin/ovbDashboard/ActivityErrors'),
    beforeEnter: authGuard,
    // should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'Activity Errors', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-errors' } }
  },
  {
    // WHERE IS THIS USED?????
    path: '/messaging-manager',
    name: 'MessagingManager',
    props: true,
    component: () => import('@/views/admin/messaging/MessagingManager'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin'], label: 'Messaging Manager', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'messaging-manager' } }
  },
  {
    // WHERE IS THIS USED?????
    path: '/help-topic-manager',
    name: 'HelpTopicManager',
    props: true,
    component: () => import('@/views/admin/helpTopicManager/HelpTopicManager'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin'] }
  },
  {
    // WHERE IS THIS USED?????
    path: '/help-topic-manager/:helpTopicId/',
    name: 'HelpTopicManagerDetail',
    props: true,
    component: () => import('@/views/admin/helpTopicManager/HelpTopicManagerDetail'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin'] }
  },
  {
    path: '/admin/manager-manager',
    name: 'ManagerManager',
    props: true,
    component: () => import('@/views/admin/managerManager/ManagerManager'),
    beforeEnter: authGuard,
    // should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'Manager Manager', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'manager-manager' } }
  },
  {
    path: '/admin/new-dashboard',
    redirect: '/ovb-dashboard'
  },
  {
    path: '/admin/dev-dashboard',
    name: 'DevDashboard',
    props: true,
    component: () => import('@/views/admin/devDashboard/DevDashboard'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin'], label: 'Dev Dashboard', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'dev-dashboard' } }
  },
  {
    path: '/admin/deployment-dashboard',
    redirect: '/admin/dev-dashboard'
  },
  {
    path: '/admin/activity-language-manager-manager',
    name: 'ActivityLanguageManagerManager',
    props: true,
    component: () => import('@/views/admin/activityLanguageManager/ActivityLanguageManagerManager'),
    beforeEnter: authGuard,
    // should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin', 'p:manage-game-language'], label: 'Activity Language Manager Manager', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-language-manager-manager' } }
  },
  {
    path: '/admin/activity-language-manager',
    name: 'ActivityLanguageManager',
    props: true,
    component: () => import('@/views/admin/activityLanguageManager/ActivityLanguageManager'),
    beforeEnter: authGuard,
    // should be p:super-admin, so good
    meta: { needsPermission: ['p:manage-game-language', 'p:super-admin'], label: 'Activity Language Manager', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-language-manager' } }
  },
  {
    path: '/admin/demo-center',
    name: 'DemoCenter',
    props: true,
    component: () => import('@/views/admin/demoCenter/DemoCenter'),
    beforeEnter: authGuard,
    // ??????? kevin set this one, so fine??
    meta: { needsPermission: ['p:demo', 'p:super-admin'], label: 'Demo Center', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'demo-center' } }
  },
  {
    path: '/admin/support-center',
    name: 'SupportCenter',
    props: true,
    component: () => import('@/views/admin/supportCenter/SupportCenter'),
    beforeEnter: authGuard,
    // ??????? kevin set this one, so fine??
    meta: { needsPermission: ['p:app-tenant-group-admin', 'p:super-admin'], label: 'Support Center', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'support-center' } }
  },
  {
    path: '/admin/app-tenant-group-manager/:appTenantGroupId',
    name: 'AppTenantGroupManagerDetail',
    props: true,
    component: () => import('@/views/admin/appTenantGroupManager/AppTenantGroupManagerDetail'),
    beforeEnter: authGuard,
    // should be p:super-admin, so good
    meta: { needsPermission: ['p:super-admin'], label: 'App Tenant Group Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'appTenantGroupId' } }
  },
  {
    path: '/activity-language-manager/activity-detail/:playKey',
    name: 'ActivityLanguageManagerDetail',
    props: true,
    component: () => import('@/views/admin/activityLanguageManager/ActivityLanguageManagerDetail'),
    beforeEnter: authGuard,
    // ??????? kevin set this one, so fine??
    meta: { needsPermission: ['p:manage-game-language', 'p:super-admin'], label: 'Activity Language Manager Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-language-manager-detail' } }
  },
  {
    path: '/activity-manager',
    name: 'ActivityManager',
    props: true,
    component: () => import('@/views/admin/activityManager/ActivityManager'),
    beforeEnter: authGuard,
    // ??????? kevin set this one, so fine??
    meta: { needsPermission: ['p:super-admin', 'p:ovb-activity-developer'], label: 'Activity Manager', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-manager' } }
  },
  {
    path: '/activity-manager/activity-detail/:activityName/:playKey/',
    name: 'ActivityDetailByPlayKey',
    props: true,
    component: () => import('@/views/admin/activityManager/ActivityDetailByPlayKey'),
    beforeEnter: authGuard,
    // ??????? kevin set this one, so fine??
    meta: { needsPermission: ['p:super-admin', 'p:ovb-activity-developer'], label: 'Activity Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'activityName' } }
  },
  {
    path: '/activity-manager/activity-version-detail/:gameId/',
    name: 'ActivityDetailById',
    props: true,
    component: () => import('@/views/admin/activityManager/ActivityDetailById'),
    beforeEnter: authGuard,
    // ??????? kevin set this one, so fine??
    meta: { needsPermission: ['p:super-admin', 'p:ovb-activity-developer'], label: 'Activity Version', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-version' } }
  },
  {
    path: '/activity-manager/session-tester/:sessionId/',
    name: 'ActivitySessionTester',
    props: true,
    component: () => import('@/views/admin/activityManager/ActivitySessionTester'),
    beforeEnter: authGuard,
    // ??????? kevin set this one, so fine??
    meta: { needsPermission: ['p:super-admin', 'p:ovb-activity-developer'], label: 'Session Tester', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'session-tester' } }
  },
  {
    path: '/activity-categorization',
    name: 'ActivityCategorizationManager',
    props: true,
    component: () => import('@/views/admin/activityCategorization/ActivityCategorizationManager'),
    beforeEnter: authGuard,
    // permissions good, should change URL to /admin/etc
    meta: { needsPermission: ['p:super-admin'], label: 'Activity Categorization', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-categorization' } }
  },
  {
    path: '/activity-categorization/library/:libraryId/',
    name: 'LibraryDetail',
    props: true,
    component: () => import('@/views/admin/activityCategorization/LibraryDetail'),
    beforeEnter: authGuard,
    // permissions good, should change URL to /admin/etc
    meta: { needsPermission: ['p:super-admin'], label: 'Library Detail', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'library-detail' } }
  },
  {
    path: '/activity-categorization/therapy-category/:therapyCategoryKey/',
    name: 'TherapyCategoryDetail',
    props: true,
    component: () => import('@/views/admin/activityCategorization/TherapyCategoryDetail'),
    beforeEnter: authGuard,
    // permissions good, should change URL to /admin/etc
    meta: { needsPermission: ['p:super-admin'], label: 'Therapy Category', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'therapy-category' } }

  },
  {
    path: '/activity-categorization/cognitive-domain/:cognitiveDomainKey/',
    name: 'CognitiveDomainDetail',
    props: true,
    component: () => import('@/views/admin/activityCategorization/CognitiveDomainDetail'),
    beforeEnter: authGuard,
    // permissions good, should change URL to /admin/etc
    meta: { needsPermission: ['p:super-admin'], label: 'Cognitive Domain', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'cognitive-domain' } }
  },
  {
    path: '/activity-configuration-library',
    name: 'ActivityConfigurationLibrary',
    props: true,
    component: () => import('@/views/admin/activityConfiguration/ActivityConfigurationLibrary'),
    beforeEnter: authGuard,
    // permissions good, todo: should change URL to /admin/etc
    meta: { needsPermission: ['p:super-admin'], label: 'Activity Configuration Library', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-configuration-library' } }
  },
  {
    path: '/activity-configuration/:playKey/:libraryId',
    name: 'LibraryGameConfigurationSummary',
    props: true,
    component: () => import('@/views/admin/activityConfiguration/LibraryGameConfigurationSummary'),
    beforeEnter: authGuard,
    // permissions good, todo: should change URL to /admin/etc
    meta: { needsPermission: ['p:super-admin'], label: 'Activity Configuration', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-configuration' } }
  },
  {
    path: '/admin/video-manager',
    name: 'WeMoveManager',
    props: true,
    component: () => import('@/views/admin/weMoveManager/WeMoveManager'),
    beforeEnter: authGuard,
    // permissions good
    meta: { needsPermission: ['p:super-admin'], label: 'Video Manager', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'video-manager' } }
  },
  {
    path: '/admin/video-manager/:weMoveKey/',
    name: 'WeMoveManagerDetail',
    props: true,
    component: () => import('@/views/admin/weMoveManager/WeMoveManagerDetail'),
    beforeEnter: authGuard,
    // permissions good
    meta: { needsPermission: ['p:super-admin'], label: 'Video Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'weMoveKey' } }
  },
  {
    path: '/admin/worksheet-manager',
    name: 'WorksheetManager',
    props: true,
    component: () => import('@/views/admin/worksheetManager/WorksheetManager'),
    beforeEnter: authGuard,
    // permissions good
    meta: { needsPermission: ['p:super-admin'], label: 'Worksheet Manager', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'worksheet-manager' } }
  },
  {
    path: '/admin/worksheet-manager/:worksheetKey/',
    name: 'WorksheetManagerDetail',
    props: true,
    component: () => import('@/views/admin/worksheetManager/WorksheetManagerDetail'),
    beforeEnter: authGuard,
    // permissions good
    meta: { needsPermission: ['p:super-admin'], label: 'Worksheet', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'worksheetKey' } }
  },
  {
    path: '/clinic-playlists',
    name: 'ClinicGamePlayLists',
    props: true,
    component: () => import('@/views/clinic/ClinicGamePlayLists'),
    beforeEnter: authGuard,
    // permissions good
    meta: { needsPermission: ['p:super-admin', 'm:clinic', 'p:support'], label: 'Clinic Playlists', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'clinic-playlists' } }
  },
  {
    path: '/clinic-playlists/:playlistName/:gamePlayListId',
    name: 'ManageClinicActivityPlaylist',
    props: true,
    component: () => import('@/views/clinic/ManageClinicActivityPlaylist'),
    beforeEnter: authGuard,
    // permissions good
    meta: { needsPermission: ['p:super-admin', 'm:clinic', 'p:support'], label: 'Clinic Playlist', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'playlistName' } }
  },
  // {
  //   path: '/silver/gameroom',
  //   name: 'GameRoomSilver',
  //   props: true,
  //   component: () => import('@/views/silver/GameRoomSilver'),
  //   beforeEnter: authGuard
  // },
  // {
  //   path: '/silver/gameroom/:activityKey',
  //   name: 'ActivitySilver',
  //   props: true,
  //   component: () => import('@/views/silver/ActivitySilver'),
  //   beforeEnter: authGuard
  // },
  // {
  //   path: '/silver/gameroom/:activityKey/:playKey',
  //   name: 'GameSilver',
  //   props: true,
  //   component: () => import('@/views/silver/GameSilver'),
  //   beforeEnter: authGuard
  // },
  {
    path: '/clinic-trial/clinic-trial-home',
    name: 'ClinicTrial',
    props: true,
    component: PagePlaceholder,
    beforeEnter: authGuard
    // todo: clinic
  },
  {
    path: '/clinic-trial/trial-gameplay',
    name: 'TrialGameplay',
    props: true,
    component: PagePlaceholder,
    beforeEnter: authGuard
    // todo: clinic
  },
  {
    path: '/clinic-trial/pricing',
    name: 'ClinicPricing',
    props: true,
    component: PagePlaceholder,
    beforeEnter: authGuard
    // todo: clinic
  },
  {
    path: '/clinic-trial/purchase-upgrade',
    name: 'PurchaseUpgrade',
    props: true,
    component: PagePlaceholder,
    beforeEnter: authGuard
    // todo: clinic
  },
  {
    path: '/activity-explorer',
    name: 'ActivityExplorer',
    props: true,
    component: () => import('@/views/clinic/ActivityExplorer'),
    beforeEnter: authGuard,
    // permissions good
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Activity Explorer', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'activity-explorer' } }
  },
  // {
  //   path: '/activity-explorer/:activityName/:playKey',
  //   name: 'ActivityExplorerDetail',
  //   props: true,
  //   component: () => import('@/views/clinic/ActivityExplorerDetail'),
  //   beforeEnter: authGuard,
  //   meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Activity Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'activityName' } }
  // },
  {
    path: '/activity-explorer/:activityId/:activityName',
    name: 'ActivityExplorerDetail',
    props: true,
    component: () => import('@/views/clinic/ActivityExplorerDetail'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Activity Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'activityName' } }
  },
  {
    path: '/worksheet-explorer',
    name: 'WorksheetExplorer',
    props: true,
    component: () => import('@/views/clinic/WorksheetExplorer'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Worksheet Explorer', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'worksheet-explorer' } }
  },
  {
    path: '/worksheet-explorer/:worksheetLabel/:worksheetKey',
    name: 'WorksheetExplorerDetail',
    props: true,
    component: () => import('@/views/clinic/WorksheetExplorerDetail'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Worksheet Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'worksheetLabel' } }
  },
  {
    path: '/video-explorer',
    name: 'WeMoveExplorer',
    props: true,
    component: () => import('@/views/clinic/VideoExplorer'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Video Explorer', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'video-explorer' } }
  },
  {
    path: '/clinic-videos',
    name: 'ClinicVideos',
    props: true,
    component: () => import('@/views/clinic/ClinicVideos'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Clinic Videos', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'clinic-videos' } }
  },
  {
    path: '/video-explorer/:targetName/:targetKey',
    name: 'VideoDetail',
    props: true,
    component: () => import('@/views/VideoDetail'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Video Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'targetName' } }
  },
  {
    path: '/clinic/clinic-home/patient-overview/:patientName/:patientId/account-management/choose-avatar',
    name: 'ClinicAvatarSelector',
    props: true,
    component: () => import('@/views/clinic/UserAvatarSelector.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin', 'p:support', 'm:patient', 'm:clinic'], label: 'Choose an Avatar', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'choose-avatar' } }
  },
  {
    path: '/my-dashboard/choose-avatar/:patientId',
    name: 'PatientAvatarSelector',
    props: true,
    component: () => import('@/views/clinic/UserAvatarSelector.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin', 'p:support', 'm:patient', 'm:clinic'], label: 'Choose an Avatar', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'choose-avatar' } }
  },
  {
    path: '/my-dashboard/:targetName/:targetKey',
    name: 'PatientVideoDetail',
    props: true,
    component: () => import('@/views/VideoDetail'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:patient', 'm:clinic'], label: 'Video Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'targetName' } }
  },
  // {
  //   path: '/patient/patient-dashboard/:targetName/:targetKey',
  //   name: 'PatientVideoDetail',
  //   props: true,
  //   component: () => import('@/views/VideoDetail'),
  //   beforeEnter: authGuard,
  //   meta: { needsPermission: ['m:patient', 'm:clinic'], label: 'Video Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'targetName' } }
  // },
  {
    path: '/clinic/clinic-home',
    name: 'ClinicDashboard',
    props: true,
    component: () => import('@/views/clinic/ClinicDashboard'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Clinic Dashboard', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'clinic-dashboard' } }
  },
  {
    path: '/clinic/notifications',
    name: 'ClinicNotificationManager',
    props: true,
    component: () => import('@/views/clinic/Notifications/ClinicNotificationManager'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic', 'p:super-admin'], label: 'Notification Manager', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'notification-manager' } }
  },
  {
    path: '/clinic/notifications/create',
    name: 'ClinicNotificationBuilder',
    props: true,
    component: () => import('@/views/clinic/Notifications/ClinicNotificationBuilder'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Create Notification', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'create-notification' } }
  },
  {
    path: '/clinic/patient-roster',
    name: 'PatientRoster',
    props: true,
    component: () => import('@/views/clinic/PatientRoster'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Patient Roster', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'patient-roster' } }
  },
  {
    path: '/clinic/patient-roster-additions',
    name: 'PatientRosterAdditions',
    props: true,
    component: () => import('@/views/clinic/PatientRosterAdditions.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Patient Roster', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'patient-roster' } }
  },
  {
    path: '/clinic/clinic-roster',
    name: 'ClinicRoster',
    props: true,
    component: () => import('@/views/clinic/ClinicRoster'),
    beforeEnter: authGuard,
    // good, all clinicians should be able to see this + super-admin
    meta: { needsPermission: ['m:clinic'], label: 'Clinic Roster', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'clinic-roster' } }
  },
  {
    path: '/clinic/clinic-roster/:clinicianName/:clinicianId/account-management',
    name: 'ClinicianAccountManagement',
    props: true,
    component: () => import('@/views/clinic/ClinicianAccountManagement.vue'),
    beforeEnter: authGuard,
    // good, all clinicians should be able to see this (super-admin must impersonate to get here) -- individual controls should be restricted
    meta: { needsPermission: ['m:clinic', 'm:admin'], label: 'Clinician Acct Mgmt', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: true, key: 'clinicianName' } }
  },
  {
    path: '/clinic/quick-start',
    name: 'QuickStart',
    props: true,
    component: () => import('@/views/clinic/QuickStart'),
    beforeEnter: authGuard,
    // good, all clinic and super
    meta: { needsPermission: ['m:clinic'], label: 'Quick Start', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, key: 'quick-start' } }
  },
  {
    path: '/clinic/clinic-home/patient-detail/:patientName/:patientId',
    name: 'PatientTherapyDashboard',
    props: true,
    component: () => import('@/views/clinic/PatientTherapyDashboard'),
    beforeEnter: authGuard,
    // only needs clinic
    meta: { needsPermission: ['m:clinic'], label: 'Patient Detail', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, label: 'patient: ', key: 'patientName' } }
  },
  {
    path: '/clinic/clinic-home/patient-overview/:patientName/:patientId',
    name: 'ClinicPatientOverview',
    props: true,
    component: () => import('@/views/clinic/ClinicPatientOverview'),
    beforeEnter: authGuard,
    // only needs clinic
    meta: { needsPermission: ['m:clinic'], label: 'Patient Overview', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: true, label: 'patient: ', key: 'patientName' } }
  },
  {
    path: '/clinic/clinic-home/patient-overview/:patientName/:patientId/patient-notes',
    name: 'ClinicPatientNotes',
    props: true,
    component: () => import('@/views/clinic/ClinicPatientNotes'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Patient Notes', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'patient-notes' } }
  },
  {
    path: '/clinic/clinic-home/patient-overview/:patientName/:patientId/account-management',
    name: 'ClinicPatientManagement',
    props: true,
    component: () => import('@/views/clinic/ClinicPatientManagement'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Patient Acct Mgmt', stickyNav: true, requiresAuth: false, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'account-management ' } }
  },
  {
    path: '/clinic/clinic-home/patient-overview/:patientName/:patientId/manage-patient-worksheets',
    name: 'PatientWorksheetManagement',
    props: true,
    component: () => import('@/views/clinic/PatientWorksheetManagement'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Manage Worksheets', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'manage-patient-worksheets' } }
  },
  {
    path: '/clinic/clinic-home/patient-overview/:patientName/:patientId/manage-patient-videos',
    name: 'PatientMotionManagement',
    props: true,
    component: () => import('@/views/clinic/PatientMotionManagement'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Manage Videos', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'manage-patient-videos' } }
  },
  {
    path: '/clinic/clinic-home/patient-overview/:patientName/:patientId/manage-patient-activities',
    name: 'PatientProgramManagement',
    props: true,
    component: () => import('@/views/clinic/PatientActivityManagement'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:clinic'], label: 'Manage Activities', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'manage-patient-activities' } }
  },
  {
    path: '/patient/patient-dashboard',
    name: 'PatientDashboard',
    props: true,
    component: () => import('@/views/clinic/PatientDashboard'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:patient', 'm:clinic'], label: 'Patient Dashboard', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'patient-dashboard' } }
  },
  {
    path: '/my-dashboard',
    name: 'MyDashboard',
    props: true,
    component: () => import('@/views/clinic/MyDashboard.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:patient', 'm:clinic'], label: 'My Dashboard', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'my-dashboard' } }
  },
  {
    path: '/patient/patient-gameplay',
    name: 'PatientGameplay',
    props: true,
    component: PagePlaceholder,
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:patient', 'm:clinic', 'p:super-admin'], label: 'Patient Gameplay', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'patient-gameplay' } }
  },
  {
    path: '/play-activity-configuration/:gameSessionType/:gameConfigurationId',
    name: 'PlayActivityConfiguration',
    props: true,
    component: () => import('@/views/PlayActivityConfiguration'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['m:patient', 'm:clinic', 'p:super-admin'], label: 'Activity Config', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'play-activity-configuration' } }
  },
  {
    path: '/admin/errors',
    name: 'ErrorViewTemp',
    props: true,
    component: () => import('@/views/ErrorViewTemp'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:super-admin', 'p:ovb-activity-developer', 'p:support'], label: 'ERRORS', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'Error List' } }
  },
  {
    path: '/admin/testing',
    name: 'TestPage',
    props: true,
    component: () => import('@/views/admin/TestPage'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:support', 'p:super-admin'], label: 'Test Page', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'test-page' } }
  },
  {
    path: '/brand',
    name: 'BrandView',
    props: true,
    component: () => import('@/views/BrandView.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:support', 'p:super-admin'], label: 'Test Page', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'brand-view' } }
  },
  {
    path: '/triangle',
    name: 'TriangleSubs',
    props: true,
    component: () => import('@/views/TriangleSubs.vue'),
    beforeEnter: authGuard,
    meta: { needsPermission: ['p:support', 'p:super-admin'], label: 'Test Page', stickyNav: true, requiresAuth: true, transitionPage: 'fade', breadcrumb: { dynamic: false, label: 'triangle-subs' } }
  },
  {
    path: '/OvbDashboard',
    redirect: '/ovb-dashboard'
  },
  {
    path: '*',
    name: 'NotFound404',
    component: () => import('@/views/NotFound404.vue')
  }
]

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior (to, from, savedPosition) {
    return { x: 0, y: 0 }
  }
})

// router.beforeEach((to, from, next) => {
//   if ((to.path.includes('/clinic/') || (to.path.includes('/admin/'))) && store.state.user.isPatient) next({ name: 'PatientDashboard' })
//   else next()
// })

export default router
